// Client-side half of demo mode.
//
// Two jobs:
//   1. Stop the writes that go from the browser straight to Supabase Auth and
//      never touch our API (password-reset email, password change). The server
//      middleware cannot see those, so they have to be refused here.
//   2. Own the copy shown when any write is refused, and the tiny event bus that
//      lets the non-React api.js raise the popup.
//
// Set REACT_APP_DEMO_MODE=true alongside the server's DEMO_MODE.
export const DEMO_MODE = String(process.env.REACT_APP_DEMO_MODE).toLowerCase() === 'true';

// Short line shown inline on the form itself. The popup carries the detail.
export const DEMO_SHORT = 'Not available in this demo.';

// What the popup says, per action. `title` names the action so it is obvious
// which button was refused; `body` explains what would have happened for real.
// Keyed by the API call, matched in order — first hit wins.
const NOTICES = [
  {
    match: (m, p) => m === 'POST' && p.startsWith('/api/catalog/resellers'),
    title: 'Adding a reseller is disabled',
    body: 'This is a read-only demo, so new resellers cannot be added. Nothing was saved and the reseller list is unchanged — everything else on this page works as normal.',
  },
  {
    match: (m, p) => m === 'POST' && p.startsWith('/api/catalog/products'),
    title: 'Adding a product is disabled',
    body: 'This is a read-only demo, so new products cannot be added to the catalogue. Nothing was saved — browsing, filtering and reporting on the existing products all work as normal.',
  },
  {
    match: (m, p) => p.startsWith('/api/alerts') && p.endsWith('/run'),
    title: 'Running an alert is disabled',
    body: 'Alerting is switched off for this demo. No rules were checked and no support tickets were raised.',
  },
  {
    match: (m, p) => m === 'POST' && p.startsWith('/api/alerts'),
    title: 'Creating an alert rule is disabled',
    body: 'Alerting is switched off for this demo, so rules cannot be created. Your rule was not saved and nothing will be monitored or ticketed.',
  },
  {
    match: (m, p) => (m === 'PATCH' || m === 'DELETE') && p.startsWith('/api/alerts'),
    title: 'Changing an alert rule is disabled',
    body: 'Alerting is switched off for this demo, so rules cannot be edited or removed. Nothing was changed.',
  },
  {
    match: (m, p) => m === 'POST' && p.startsWith('/api/users/create'),
    title: 'Creating a user is disabled',
    body: 'This is a read-only demo, so new accounts cannot be created. Nothing was saved and no invitation email was sent.',
  },
  {
    match: (m, p) => p.includes('/reset-mfa'),
    title: 'Resetting two-factor authentication is disabled',
    body: 'This is a read-only demo, so two-factor authentication cannot be reset from here. Nothing was changed and the account is untouched.',
  },
  {
    match: (m, p) => p.includes('/password') && p.startsWith('/api/users'),
    title: 'Setting a password is disabled',
    body: 'This is a read-only demo, so passwords cannot be set from here. Nothing was changed and the account still uses its existing password.',
  },
  {
    match: (m, p) => p.startsWith('/api/users'),
    title: 'Changing a user is disabled',
    body: 'This is a read-only demo, so accounts cannot be edited, deactivated or removed. Nothing was saved and the user list is unchanged.',
  },
  {
    match: (m, p) => m === 'POST' && p.startsWith('/api/system-status/run'),
    title: 'Running a system check is disabled',
    body: 'This is a read-only demo, so health checks cannot be triggered on demand. No check ran — the results already on screen are still shown.',
  },
  {
    match: (m, p) => m === 'PATCH' && p.startsWith('/api/auth/me'),
    title: 'Editing your profile is disabled',
    body: 'This is a read-only demo, so profile details cannot be updated. Your change was not saved.',
  },
];

// Browser-side writes that never reach the API, raised by key instead of by URL.
const DIRECT_NOTICES = {
  'password-reset': {
    title: 'Password reset is disabled',
    body: 'This is a read-only demo, so password resets are unavailable. No email was sent. Please use the demo sign-in details you were given.',
  },
  'password-change': {
    title: 'Changing your password is disabled',
    body: 'This is a read-only demo, so passwords cannot be changed. Your password is unchanged — carry on with the sign-in details you were given.',
  },
};

const FALLBACK = {
  title: 'This action is disabled',
  body: 'This is a read-only demo, so nothing you change here is saved. Feel free to explore — every screen, filter, chart and export works in full; only saving is switched off.',
};

export function noticeFor(method, path) {
  const found = NOTICES.find((n) => n.match(String(method).toUpperCase(), path));
  return found ? { title: found.title, body: found.body } : FALLBACK;
}

// --- popup bus ----------------------------------------------------------
// api.js is a plain module with no access to React state, so it publishes here
// and <DemoNotice/> (mounted once at the app root) renders whatever arrives.
let listener = null;

export function onDemoNotice(cb) {
  listener = cb;
  return () => { if (listener === cb) listener = null; };
}

export function showDemoNotice(notice) {
  if (listener) listener(notice);
}

// Refuse a browser-side write: raise the popup, then throw so the calling form
// stops and shows its short inline message.
export function refuseInDemo(key) {
  if (!DEMO_MODE) return;
  showDemoNotice(DIRECT_NOTICES[key] || FALLBACK);
  throw new Error(DEMO_SHORT);
}
